import React, { memo, useCallback } from 'react';

import { Box, Typography } from '@mui/material';
import { PlusIcon } from '@sharedCR/coreDesignSystem/icons';
import AddButton from 'app/shared/components/common/AddButton';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { deleteByIndex, setIn } from 'utils/data';
import { extendObjWithUUID } from 'utils/uuid';

import { loanManagementTestIds } from '../../loanManagement.testIds';
import useFormContext from '../hooks/useFormContext';

import SupportingDocumentRow from './components/SupportingDocumentRow';
import {
  EMPTY_DOCUMENT_TYPES_OPTIONS,
  EMPTY_SUPPORTING_DOCUMENTS,
} from './SupportingDocumentsField.constants';
import { getEmptySupportingDocument } from './SupportingDocumentsField.utils';

const SupportingDocumentsField = ({
  fieldPath,
  description,
  documentTypesOptions = EMPTY_DOCUMENT_TYPES_OPTIONS,
  vendorDocType = null,
  disabled = false,
}) => {
  const { form, setInForm, setValidationErrors } = useFormContext();

  const rows = _.get(form, fieldPath) ?? EMPTY_SUPPORTING_DOCUMENTS;

  const onAddRow = useCallback(
    () =>
      setInForm(fieldPath, [...rows, extendObjWithUUID(getEmptySupportingDocument(vendorDocType))]),
    [setInForm, fieldPath, rows, vendorDocType],
  );

  const onDeleteRow = useCallback(
    rowIndex => {
      setInForm(fieldPath, deleteByIndex(rows, rowIndex));
      setValidationErrors(errors =>
        setIn(errors, fieldPath, deleteByIndex(_.get(errors, fieldPath, []), rowIndex)),
      );
    },
    [setInForm, setValidationErrors, fieldPath, rows],
  );

  return (
    <Box className="flex flex-col gap-1">
      <Box className="flex items-center justify-between">
        <Typography variant="medium14">Supporting documents</Typography>
        {!disabled && (
          <AddButton
            ariaLabel="Add supporting document"
            dataTestId={loanManagementTestIds.getAddSupportingDocumentButtonId(fieldPath)}
            icon={PlusIcon}
            styleConfig="relative"
            onClick={onAddRow}
          />
        )}
      </Box>
      <Typography color="text.secondary" variant="regular12">
        {description}
      </Typography>
      {rows.map((row, rowIndex) => (
        <SupportingDocumentRow
          disabled={disabled}
          documentTypesOptions={documentTypesOptions}
          key={row.id}
          rowIndex={rowIndex}
          rowPath={`${fieldPath}[${rowIndex}]`}
          vendorDocType={vendorDocType}
          onDelete={onDeleteRow}
        />
      ))}
    </Box>
  );
};

SupportingDocumentsField.propTypes = {
  fieldPath: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  documentTypesOptions: PropTypes.array,
  vendorDocType: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
  }),
  disabled: PropTypes.bool,
};

export default memo(SupportingDocumentsField);
